import { Menu, Transition } from '@headlessui/react';
import { Fragment, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import Link from 'next/link';
import { ChevronDown, Info } from 'lucide-react';
import settings, { priceFormater } from '@/settings/index';
import { priceFormatSuperscript } from '@/lib/price';
import { useProductPageContext } from '@/components/v2/product/hooks/useProductPageContext';
import { KeyboardOptionProps } from '../types';
import { matchesStoreLanguage } from '../helpers';
import i18n from '@/settings/i18n';

const t = i18n.init('product');

const PreferredLanguageHint = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isOpen = isHovered || isPinned;

  useEffect(() => {
    if (!isPinned) {
      return undefined;
    }

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsPinned(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsPinned(false);
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isPinned]);

  return (
    <div ref={containerRef} className="relative flex shrink-0 items-center">
      <button
        type="button"
        aria-label={t`More information`}
        aria-expanded={isOpen}
        onClick={() => setIsPinned((pinned) => !pinned)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
        className="flex items-center focus:outline-none"
      >
        <Info className="h-3.5 w-3.5" strokeWidth={2} />
      </button>

      {isOpen && (
        <div
          role="tooltip"
          className="absolute right-0 top-full z-40 mt-2 w-[240px] rounded-xl border border-gray-700/10 bg-white p-3 text-center text-xs font-normal leading-snug text-gray-700 shadow-[0_4px_10px_0_rgba(7,_27,_58,_0.25)]"
        >
          {t`Not the usual keyboard layout for your country. Double-check before ordering.`}
        </div>
      )}
    </div>
  );
};

const ROW_CLASSNAME =
  'flex w-full items-center justify-between gap-12 rounded px-3 py-2.5 text-left text-xs';

const KeyboardOption = ({
  variant,
  variantSlug,
  selectedOption,
}: KeyboardOptionProps) => {
  const { productId, selectedVariant, variantTree } = useProductPageContext();

  if (!selectedVariant) {
    return null;
  }

  // Resolved exactly as the tile options do: each value's offer comes from the
  // variant tree, and values with no reachable offer are dropped.
  const options = variant.values.flatMap((value) => {
    const offer = variantTree.get(selectedVariant.variants, [
      { variant: variant.variant, value },
    ]);

    if (offer.shouldHide) {
      return [];
    }

    return [{ value, offer, label: value.displayValue ?? value.value }];
  });

  if (!options.length) {
    return null;
  }

  const selectedLabel =
    options.find((option) => option.value.value === selectedOption)?.label ??
    selectedOption;

  const isRecommended = matchesStoreLanguage(selectedLabel, settings.language);

  return (
    <div className="inline-block">
      <Menu as="div" className="relative block">
        {({ open }) => (
          <>
            <Menu.Button
              id={`e2e-pdp-${variantSlug}-dropdown`}
              className={cn(
                'flex min-h-[40px] w-full min-w-[300px] items-center justify-between gap-2 rounded border px-3 py-1.5 text-xs focus:outline-none',
                open
                  ? 'border-gray-700'
                  : 'border-gray-700/25 hover:border-gray-700',
              )}
            >
              <span className="flex flex-col items-start leading-tight">
                <span className="font-bold">{selectedLabel}</span>
                {isRecommended && (
                  <span className="text-xxs font-normal text-gray-700/60">
                    {t`Recommended for you`}
                  </span>
                )}
              </span>
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 transition-transform duration-200 ease-in-out',
                  { 'rotate-180': open },
                )}
                strokeWidth={2}
              />
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute left-0 z-30 mt-1 w-max min-w-[300px] origin-top overflow-hidden rounded-md border border-gray-700/20 bg-white p-2 shadow-[0_4px_10px_0_rgba(7,_27,_58,_0.25)] focus:outline-none">
                {options.map(({ value, offer, label }, index) => {
                  const isSelected = value.value === selectedOption;

                  // A divider is drawn ahead of every row but the first.
                  const showDivider = index > 0;

                  return (
                    <Fragment key={value.value}>
                      {showDivider && (
                        <div className="mx-2 border-b border-[#F0F3F7]" />
                      )}
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            href={`/collections/${encodeURIComponent(
                              productId,
                            )}?skuId=${encodeURIComponent(offer.skuId)}`}
                            shallow
                            scroll={false}
                            id={`e2e-pdp-${variantSlug}-${label}`}
                            className={cn(ROW_CLASSNAME, {
                              'bg-[#F0F3F7]': isSelected,
                              'bg-[#F0F3F7]/50': active && !isSelected,
                            })}
                          >
                            <span className={cn({ 'font-bold': isSelected })}>
                              {label}
                            </span>
                            <span className="shrink-0" translate="no">
                              {priceFormatSuperscript(
                                priceFormater.short(offer.price),
                              )}
                            </span>
                          </Link>
                        )}
                      </Menu.Item>
                    </Fragment>
                  );
                })}
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>

      {/* Shown only for a layout in another language — a deliberate choice a
          shopper may still want, so this warns rather than blocks. */}
      {!isRecommended && (
        <div className="mx-2 flex w-max items-center justify-between gap-2 rounded-b bg-[#FDF4E7] px-2 text-xxs font-medium text-[#8A5A0B]">
          {t`This keyboard may not be your preferred language.`}
          <PreferredLanguageHint />
        </div>
      )}
    </div>
  );
};

export default KeyboardOption;
